import React from "react"
import moment from "moment"
import { pgaComTld } from "../../../utils"
import { LEVEL_NAMES, LEVEL_ORDER } from "./constants/levels"

export const SUSPENSION_CODES_WITH_TERMINATION = ["GPTP", "NPDU"]
export const TERMINATION_WINDOW_DURATION = 2
export const TERMINATION_WINDOW_UNIT = "years"

/** GPTP banner copy: suspended (default) vs Acceptable Progress Termination. */
export const GPTP_MESSAGE_VARIANT = {
  SUSPENDED: "suspended",
  TERMINATED: "terminated",
}

// Contact information constants
const CONTACT_INFO = {
  BILLING_PHONE: {
    display: "(888) 263-5285",
    href: "tel:+18882635285",
  },
  getInvoiceUrl: () => `https://${pgaComTld}/member/account/invoices`,
}

const hasTerminationWindow = code => {
  return SUSPENSION_CODES_WITH_TERMINATION.includes(code)
}

export const calculateSuspensionData = suspension => {
  if (!suspension || !suspension.startDate) {
    return null
  }

  const startDate = moment(suspension.startDate)
  const endDate = suspension.endDate ? moment(suspension.endDate) : null
  const isActive = !endDate
  const hasTermination = hasTerminationWindow(suspension.code)

  const terminationDate =
    hasTermination && isActive
      ? startDate
          .clone()
          .add(TERMINATION_WINDOW_DURATION, TERMINATION_WINDOW_UNIT)
      : null

  const daysUntilTermination = terminationDate
    ? terminationDate.diff(moment(), "days")
    : null

  const monthsUntilTermination = terminationDate
    ? Math.floor(daysUntilTermination / 30)
    : null
  const remainingDays = terminationDate ? daysUntilTermination % 30 : null

  return {
    code: suspension.code,
    isActive,
    hasTerminationWindow: hasTermination,
    terminationDate: terminationDate
      ? terminationDate.format("YYYY-MM-DD")
      : null,
    daysUntilTermination,
    monthsUntilTermination,
    remainingDays,
    formattedTerminationDate: terminationDate
      ? terminationDate.format("MM/DD/YYYY")
      : null,
  }
}

export const formatTimeRemaining = suspensionData => {
  if (
    !suspensionData ||
    !suspensionData.isActive ||
    !suspensionData.terminationDate
  ) {
    return null
  }

  const { monthsUntilTermination, remainingDays } = suspensionData

  if (monthsUntilTermination === 0) {
    return `${remainingDays} ${remainingDays === 1 ? "day" : "days"}`
  }

  const monthsText = `${monthsUntilTermination} ${monthsUntilTermination === 1 ? "month" : "months"}`
  const daysText =
    remainingDays > 0
      ? `, ${remainingDays} ${remainingDays === 1 ? "day" : "days"}`
      : ""

  return `${monthsText}${daysText}`
}

const getNextLevelName = currentLevelKey => {
  const currentLevelIndex = LEVEL_ORDER.indexOf(currentLevelKey)

  if (currentLevelIndex === -1) {
    return null
  }

  const nextLevelKey = LEVEL_ORDER[currentLevelIndex + 1]
  return nextLevelKey ? LEVEL_NAMES[nextLevelKey] : null
}

export const getLevelKeyAtSuspensionStart = (pgmProgress, suspension) => {
  const fallbackLevelKey = pgmProgress?.currentLevelKey || null

  if (!suspension?.startDate) {
    return fallbackLevelKey
  }

  const suspensionStart = new Date(suspension.startDate).getTime()

  if (!Number.isFinite(suspensionStart)) {
    return fallbackLevelKey
  }

  // Suspensions can outlive a just-completed level until the next sync clears them,
  // so level-specific messaging should stay anchored to the level active when the suspension began.
  const candidates = (pgmProgress?.levels || [])
    .filter(
      level => level?.key && LEVEL_ORDER.includes(level.key) && level.startDate,
    )
    .map(level => ({
      key: level.key,
      start: new Date(level.startDate).getTime(),
    }))
    .filter(
      level => Number.isFinite(level.start) && level.start <= suspensionStart,
    )
    .sort((a, b) => {
      if (a.start !== b.start) return b.start - a.start
      return LEVEL_ORDER.indexOf(b.key) - LEVEL_ORDER.indexOf(a.key)
    })

  return candidates[0]?.key || fallbackLevelKey
}

const GptpSuspendedMessages = {
  BannerMessage: () => (
    <>You are suspended for not meeting acceptable progress requirements.</>
  ),
  PopoverMessage: ({ currentLevelName, currentLevelKey }) => {
    const nextPurchaseLevelName = getNextLevelName(currentLevelKey)

    return (
      <>
        You are currently suspended for not making Acceptable Progress in the
        PGA Professional Golf Management Program. Please complete all remaining
        requirements listed under {currentLevelName || "your current level"}
        {nextPurchaseLevelName
          ? ` and purchase ${nextPurchaseLevelName}`
          : ""}{" "}
        to get back on track.
      </>
    )
  },
}

/** Acceptable Progress Termination GPTP banner / popover copy. */
const GptpTerminatedMessages = {
  BannerMessage: ({ currentLevelName }) => (
    <>
      Your membership is not current due to failure to meet Acceptable Progress.
      After completing {currentLevelName || "your current level"}, you must
      reapply to the Associate program.
    </>
  ),
  PopoverMessage: ({ currentLevelName }) => (
    <>
      Your membership is not current due to failure to meet Acceptable Progress
      in the PGA Professional Golf Management Program. Complete all remaining
      requirements under {currentLevelName || "your current level"}, then
      reapply to the Associate program before purchasing or starting the next
      level.
    </>
  ),
}

const resolveGptpMessages = (messageVariant = GPTP_MESSAGE_VARIANT.SUSPENDED) =>
  messageVariant === GPTP_MESSAGE_VARIANT.TERMINATED
    ? GptpTerminatedMessages
    : GptpSuspendedMessages

const GptpSuspension = {
  hasLearnMore: true,
  getBannerDeadlineLabel: currentLevelName =>
    `${currentLevelName} Termination Deadline`,
  ...GptpSuspendedMessages,
}

const NpduSuspension = {
  hasLearnMore: false,
  getBannerDeadlineLabel: () => "Termination Deadline",
  BannerMessage: () => (
    <>
      You are currently suspended from the PGA Professional Golf Management
      Program due to non-payment of dues. To restore your status, please{" "}
      <a
        href={CONTACT_INFO.getInvoiceUrl()}
        target="_blank"
        rel="noopener noreferrer"
      >
        click here
      </a>{" "}
      to pay your outstanding invoice, or call{" "}
      <a href={CONTACT_INFO.BILLING_PHONE.href}>
        {CONTACT_INFO.BILLING_PHONE.display}
      </a>{" "}
      to make a payment by phone.
    </>
  ),
}

const CoevSuspension = {
  hasLearnMore: false,
  getBannerDeadlineLabel: () => null,
  BannerMessage: () => (
    <>
      You are currently suspended from the PGA Professional Golf Management
      Program due to a Code of Ethics violation.
    </>
  ),
}

const SUSPENSION_TYPES = {
  GPTP: GptpSuspension,
  NPDU: NpduSuspension,
  COEV: CoevSuspension,
}

export const getSuspensionConfig = code => {
  return SUSPENSION_TYPES[code] || null
}

export const getBannerMessage = (
  suspensionData,
  currentLevelName = null,
  currentLevelKey = null,
  messageVariant = GPTP_MESSAGE_VARIANT.SUSPENDED,
) => {
  if (!suspensionData) {
    return null
  }

  const suspensionType = SUSPENSION_TYPES[suspensionData.code]
  if (!suspensionType) return null

  const MessageComponent =
    suspensionData.code === "GPTP"
      ? resolveGptpMessages(messageVariant).BannerMessage
      : suspensionType.BannerMessage

  return (
    <MessageComponent
      suspensionData={suspensionData}
      currentLevelName={currentLevelName}
      currentLevelKey={currentLevelKey}
    />
  )
}

export const getSuspensionMessage = (
  suspensionData,
  currentLevelName = null,
  currentLevelKey = null,
  messageVariant = GPTP_MESSAGE_VARIANT.SUSPENDED,
) => {
  if (!suspensionData) {
    return null
  }

  const suspensionType = SUSPENSION_TYPES[suspensionData.code]
  if (!suspensionType) return null

  const gptpMessages =
    suspensionData.code === "GPTP" ? resolveGptpMessages(messageVariant) : null
  const MessageComponent =
    gptpMessages?.PopoverMessage ||
    suspensionType.PopoverMessage ||
    suspensionType.BannerMessage

  return (
    <MessageComponent
      suspensionData={suspensionData}
      currentLevelName={currentLevelName}
      currentLevelKey={currentLevelKey}
    />
  )
}

export const shouldShowSuspensionWarning = pgmProgress => {
  if (!pgmProgress) return false
  return Boolean(pgmProgress.suspensions?.length > 0)
}

export const getSuspensionFromProgress = pgmProgress => {
  if (!shouldShowSuspensionWarning(pgmProgress)) {
    return null
  }
  const suspensionsWithData = pgmProgress.suspensions
    .map(suspension => calculateSuspensionData(suspension))
    .filter(data => data !== null)

  if (suspensionsWithData.length === 0) {
    return null
  }

  const suspensionsWithTermination = suspensionsWithData
    .filter(data => data.hasTerminationWindow && data.terminationDate)
    .sort((a, b) => {
      // Sort by soonest termination date (earlier dates first)
      return new Date(a.terminationDate) - new Date(b.terminationDate)
    })

  if (suspensionsWithTermination.length > 0) {
    return suspensionsWithTermination[0]
  }

  // For suspensions without termination windows, sort by earliest start date
  const sortedByStartDate = suspensionsWithData.sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate)
  })

  return sortedByStartDate[0]
}

export const getAllActiveSuspensions = pgmProgress => {
  if (!shouldShowSuspensionWarning(pgmProgress)) {
    return []
  }

  const suspensionsWithData = pgmProgress.suspensions
    .map(suspension => ({
      ...calculateSuspensionData(suspension),
      startDate: suspension.startDate,
    }))
    .filter(data => data !== null && data.isActive)
    .filter(data => SUSPENSION_TYPES[data.code]) // Only include supported suspension types

  // Sort by start date (earliest first)
  return suspensionsWithData.sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate)
  })
}
